import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faMousePointer } from '@fortawesome/free-solid-svg-icons';
import './MapToolbar.css';

// Custom SVG component for the Zoom to Rectangle icon
const ZoomToRectangleIcon: React.FC = () => (
  <svg 
    version="1.1" 
    xmlns="http://www.w3.org/2000/svg" 
    width="16" 
    height="16" 
    viewBox="0 0 1024 1024"
    style={{ display: 'inline-block', verticalAlign: 'text-bottom', marginLeft: '4px' }}
  >
    <g id="imRectangle">
    </g>
    <path 
      d="M896 768v128h-128v-32h-192v32h-128v-32h-192v32h-128v-128h32v-192h-32v-128h32v-192h-32v-128h128v32h192v-32h128v32h192v-32h128v128h-32v192h32v128h-32v192h32zM480 864h64v-64h-64v64zM160 864h64v-64h-64v64zM160 544h64v-64h-64v64zM224 160h-64v64h64v-64zM544 160h-64v64h64v-64zM768 224h-192v32h-128v-32h-192v32h-32v192h32v128h-32v192h32v32h192v-32h128v32h192v-32h32v-192h-32v-128h32v-192h-32v-32zM864 160h-64v64h64v-64zM864 480h-64v64h64v-64zM800 800v64h64v-64h-64z" 
      fill="currentColor"
    />
  </svg>
);

interface County {
  fips: string;
  state: string;
  state_name: string;
  county: string;
  full_name: string;
  centroid?: [number, number] | null;
  county_key?: string;
}

interface MapToolbarProps {
  onCountySelect: (countyKey: string) => void;
  onZoomToLocation: (lat: number, lng: number, zoom: number) => void;
  onFetchPolygons: (countyKey: string) => void;
  onDrawingModeChange: (isDrawing: boolean) => void;
  onSelectModeChange: (isSelectMode: boolean) => void;
  onDrawFieldBoundary?: () => void; // New handler for manual draw mode
  isDrawingMode: boolean;
  isFieldDrawingMode?: boolean; // New prop for field drawing mode
  isSelectMode: boolean;
  currentCounty?: string;
  polygonsLoaded?: boolean;
  isManualDrawMode?: boolean; // New prop for manual draw mode
  externalState?: string; // New prop for external state override
  externalCounty?: string; // New prop for external county override
  showDeleteEditButtons?: boolean;
  onDeleteFieldDrawing?: () => void;
  onEditFieldDrawing?: () => void;
  onExitFieldDrawingEdit?: () => void;
  isFieldEditingMode?: boolean;
}

const MapToolbar: React.FC<MapToolbarProps> = ({
  onCountySelect,
  onZoomToLocation,
  onFetchPolygons,
  onDrawingModeChange,
  onSelectModeChange,
  onDrawFieldBoundary,
  isDrawingMode,
  isFieldDrawingMode = false,
  isSelectMode,
  currentCounty,
  polygonsLoaded = false,
  isManualDrawMode = false,
  externalState,
  externalCounty,
  showDeleteEditButtons = false,
  onDeleteFieldDrawing,
  onEditFieldDrawing,
  onExitFieldDrawingEdit,
  isFieldEditingMode = false
}) => {
  const [counties, setCounties] = useState<County[]>([]);
  const [states, setStates] = useState<string[]>([]);
  const [selectedState, setSelectedState] = useState<string>('');
  const [selectedCounty, setSelectedCounty] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const lastExternalCountyRef = useRef<string>('');

  // Load saved state/county from localStorage on component mount
  useEffect(() => {
    const savedState = localStorage.getItem('lastSelectedState');
    const savedCounty = localStorage.getItem('lastSelectedCounty');
    
    if (savedState) {
      setSelectedState(savedState);
    }
    if (savedCounty) {
      setSelectedCounty(savedCounty);
      // Call onCountySelect to ensure currentCountyData is set in FieldMapModal
      onCountySelect(savedCounty);
    }
  }, []); // Remove onCountySelect from dependencies to prevent infinite re-calls

  // Handle external state/county overrides (from smart zoom)
  useEffect(() => {
    if (externalState && externalState !== selectedState) {
     // console.log('🌾 MapToolbar: Setting external state:', externalState);
      setSelectedState(externalState);
    }
    if (externalCounty && externalCounty !== lastExternalCountyRef.current) {
      //console.log('🌾 MapToolbar: Setting external county:', externalCounty);
      //console.log('🌾 MapToolbar: Current selectedCounty:', selectedCounty);
      //console.log('🌾 MapToolbar: Counties available:', counties.length);
      
      // Only set if counties are loaded
      if (counties.length > 0) {
        setSelectedCounty(externalCounty);
        onCountySelect(externalCounty);
        lastExternalCountyRef.current = externalCounty;
      } else {
        //console.log('🌾 MapToolbar: Counties not loaded yet, deferring county selection');
      }
    }
  }, [externalState, externalCounty]); // Only depend on external props

  // Set external county when counties data loads
  useEffect(() => {
    if (counties.length > 0 && externalCounty && externalCounty !== lastExternalCountyRef.current) {
      //console.log('🌾 MapToolbar: Counties loaded, setting external county:', externalCounty);
      setSelectedCounty(externalCounty);
      onCountySelect(externalCounty);
      lastExternalCountyRef.current = externalCounty;
    }
  }, [counties, externalCounty]);

  // Validate saved county when counties data loads
  useEffect(() => {
    if (counties.length > 0 && selectedState && selectedCounty) {
      const validCounties = getCountiesForState(selectedState);
      const countyExists = validCounties.some(county => county.county_key === selectedCounty);
      
      if (!countyExists) {
        setSelectedCounty('');
        localStorage.removeItem('lastSelectedCounty');
      }
    }
  }, [counties, selectedState, selectedCounty]);

  useEffect(() => {
    loadCountiesData();
  }, []);

  const loadCountiesData = async () => {
    try {
      const response = await fetch('/counties.json');
      const data = await response.json();
      setCounties(data);
      
      // Extract unique states
      const uniqueStates = Array.from(new Set(data.map((county: County) => county.state_name))).sort() as string[];
      setStates(uniqueStates);
      
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading counties data:', error);
      setIsLoading(false);
    }
  };

  const handleStateChange = (state: string) => {
    setSelectedState(state);
    setSelectedCounty('');
    
    // Save to localStorage
    if (state) {
      localStorage.setItem('lastSelectedState', state);
    } else {
      localStorage.removeItem('lastSelectedState');
    }
    // Clear county when state changes
    localStorage.removeItem('lastSelectedCounty');
  };

  const handleCountyChange = (countyKey: string) => {
    setSelectedCounty(countyKey);
    onCountySelect(countyKey);
    
    // Save to localStorage
    if (countyKey) {
      localStorage.setItem('lastSelectedCounty', countyKey);
    } else {
      localStorage.removeItem('lastSelectedCounty');
    }
  };

  const handleFetchPolygons = () => {
    if (selectedCounty) {
      onFetchPolygons(selectedCounty);
    }
  };

  const handleDrawingModeToggle = () => {
    onDrawingModeChange(!isDrawingMode);
  };

  const handleSelectModeToggle = () => {
    onSelectModeChange(!isSelectMode);
  };

  const handleZoomToLocation = () => {
    if (selectedCounty) {
      const county = counties.find(c => c.county_key === selectedCounty);
      if (county && county.centroid) {
        const [lng, lat] = county.centroid;
        if (typeof lng === 'number' && typeof lat === 'number') {
          onZoomToLocation(lat, lng, 10);
        } else {
          console.error('❌ Invalid centroid coordinates:', county.centroid);
        }
      } else {
        console.error('❌ County or centroid not found:', county);
      }
    }
  };

  const getCountiesForState = (stateName: string) => {
    return counties
      .filter(county => county.state_name === stateName)
      .sort((a, b) => a.county.localeCompare(b.county));
  };

  if (isLoading) {
    return <div className="map-toolbar">Loading...</div>;
  }

  return (
    <div className="map-toolbar">
      <div className="toolbar-section">
        <label htmlFor="state-select">State:</label>
        <select
          id="state-select"
          value={selectedState}
          onChange={(e) => handleStateChange(e.target.value)}
          className="toolbar-select"
        >
          <option value="">Select State</option>
          {states.map(state => (
            <option key={state} value={state}>{state}</option>
          ))}
        </select>
      </div>

      <div className="toolbar-section">
        <label htmlFor="county-select">County:</label>
        <select
          id="county-select"
          value={selectedCounty}
          onChange={(e) => handleCountyChange(e.target.value)}
          className="toolbar-select"
          disabled={!selectedState}
        >
          <option value="">Select County</option>
          {selectedState && getCountiesForState(selectedState).map(county => (
            <option key={county.county_key} value={county.county_key}>
              {county.county}
            </option>
          ))}
        </select>
      </div>

      <div className="toolbar-section">
        <button
          onClick={handleZoomToLocation}
          disabled={!selectedCounty}
          className="toolbar-button"
          title="Zoom to County"
        >
          <FontAwesomeIcon icon={faSearch} /> County
        </button>
      </div>

      {/* Fetch Field Polygons (tile server) - disabled: boundaries not accurate enough
      {!isManualDrawMode && (
        <div className="toolbar-section">
          <button
            onClick={handleFetchPolygons}
            disabled={!selectedCounty}
            className="toolbar-button"
            title={polygonsLoaded ? "Hide Field Polygons" : "Fetch Field Polygons"}
          >
            {polygonsLoaded ? "Hide Field Polygons" : "Fetch Field Polygons"}
          </button>
        </div>
      )}
      */}

      {/* Show Draw Field Boundary button only in manual draw mode */}
      {isManualDrawMode && (
        <div className="toolbar-section">
          <button
            onClick={onDrawFieldBoundary}
            className={`toolbar-button ${isFieldDrawingMode ? 'active' : ''}`}
            title={isFieldDrawingMode ? 'Exit Field Drawing Mode' : 'Draw Field Boundary'}
          >
            {isFieldDrawingMode ? 'Exit Field Drawing' : 'Draw Field Boundary'}
          </button>
        </div>
      )}

      {/* Show Delete and Edit buttons when user has drawn at least 3 points */}
      {isManualDrawMode && isFieldDrawingMode && showDeleteEditButtons && (
        <>
          {!isFieldEditingMode ? (
            <div className="toolbar-section">
              <button
                onClick={onEditFieldDrawing}
                className="toolbar-button"
                title="Edit Field Boundary"
              >
                Edit
              </button>
            </div>
          ) : (
            <div className="toolbar-section">
              <button
                onClick={onExitFieldDrawingEdit}
                className="toolbar-button active"
                title="Exit Edit Mode"
              >
                Exit Edit
              </button>
            </div>
          )}
          <div className="toolbar-section">
            <button
              onClick={onDeleteFieldDrawing}
              className="toolbar-button"
              title="Delete Field Drawing"
              style={{ backgroundColor: '#dc3545', color: 'white' }}
            >
              Delete
            </button>
          </div>
        </>
      )}

      <div className="toolbar-section">
        <button
          onClick={handleDrawingModeToggle}
          className={`toolbar-button ${isDrawingMode ? 'active' : ''}`}
          title={isFieldDrawingMode ? 'Disabled - Field Drawing Mode Active' : 'Zoom to Rectangle'}
          disabled={isFieldDrawingMode}
        >
         Zoom to <ZoomToRectangleIcon /> 
        </button>
      </div>

      {/* Select Polygons (tile server) - disabled: boundaries not accurate enough
      {!isManualDrawMode && (
        <div className="toolbar-section">
          <button
            onClick={handleSelectModeToggle}
            className={`toolbar-button ${isSelectMode ? 'active' : ''}`}
            title={isSelectMode ? 'Exit Select Mode' : 'Select a Polygon'}
            disabled={!polygonsLoaded}
          >
            <FontAwesomeIcon icon={faMousePointer} /> Select
          </button>
        </div>
      )}
      */}
      {/*currentCounty && (  .. LEAVE THIS OUT FOR NOW
        <div className="toolbar-section current-location">
          <span>Current: {currentCounty}</span>
        </div>
      )*/}
    </div>
  );
};

export default MapToolbar; 